
"use client";

import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { get, ref } from 'firebase/database';
import { db, realtimeDb } from '../firebase/clientApp';

// Define available languages
export type Language = 'en' | 'de' | 'ro' | 'fr' | 'es';

// Define translation context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: Record<string, string>;
  translate: (key: string, defaultValue?: string) => string;
  isLoading: boolean;
}

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'ro', // Default to Romanian as specified
  setLanguage: () => { },
  translations: {},
  translate: (key: string, defaultValue?: string) => defaultValue || key,
  isLoading: true,
});

// Cache for translations to avoid unnecessary Firestore/RTDB fetches
const translationCache: Record<Language, Record<string, string>> = {
  en: {},
  de: {},
  ro: {},
  fr: {},
  es: {},
};

// Initial translations for immediate use before fetching from database
const initialTranslations: Record<Language, Record<string, string>> = {
  en: {
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.services': 'Services',
    'nav.contact': 'Contact',
  },
  de: {
    'nav.home': 'Startseite',
    'nav.about': 'Über uns',
    'nav.services': 'Dienstleistungen',
    'nav.contact': 'Kontakt',
  },
  ro: {
    'nav.home': 'Acasă',
    'nav.about': 'Despre noi',
    'nav.services': 'Servicii',
    'nav.contact': 'Contact',
  },
  fr: {
    'nav.home': 'Accueil',
    'nav.about': 'À Propos',
    'nav.services': 'Services',
    'nav.contact': 'Contact',
  },
  es: {
    'nav.home': 'Inicio',
    'nav.about': 'Sobre Nosotros',
    'nav.services': 'Servicios',
    'nav.contact': 'Contacto',
  },
};

// Language provider component with performance optimizations
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get default language from environment variable or use Romanian
  const defaultLanguage = (process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE as Language) || 'ro';

  // State for current language and translations
  const [language, setLanguage] = useState<Language>(defaultLanguage);
  const [translations, setTranslations] = useState<Record<string, string>>(initialTranslations[defaultLanguage]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Add a timestamp to track language change for cache busting
  const [changeTimestamp, setChangeTimestamp] = useState<number>(Date.now());

  // Check various storage locations for language preference - memoized
  const detectLanguage = useCallback((): Language => {
    if (typeof window === 'undefined') return defaultLanguage;
    // Get from localStorage (highest priority)
    const localStorageLang = localStorage.getItem('preferred_language');
    if (localStorageLang && ['en', 'de', 'ro', 'fr', 'es'].includes(localStorageLang)) {
      return localStorageLang as Language;
    }

    // Get from sessionStorage (medium priority)
    const sessionLang = sessionStorage.getItem('preferred_language');
    if (sessionLang && ['en', 'de', 'ro', 'fr', 'es'].includes(sessionLang)) {
      return sessionLang as Language;
    }

    // Get from cookies (medium priority)
    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
      return null;
    };

    // Check both cookie names
    const nextLocaleCookie = getCookie('NEXT_LOCALE');
    if (nextLocaleCookie && ['en', 'de', 'ro', 'fr', 'es'].includes(nextLocaleCookie)) {
      return nextLocaleCookie as Language;
    }

    const preferredLangCookie = getCookie('preferred_language');
    if (preferredLangCookie && ['en', 'de', 'ro', 'fr', 'es'].includes(preferredLangCookie)) {
      return preferredLangCookie as Language;
    }

    // Get from URL parameter (lowest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && ['en', 'de', 'ro', 'fr', 'es'].includes(urlLang)) {
      return urlLang as Language;
    }

    // Return default if no preference found
    return defaultLanguage;
  }, [defaultLanguage]);

  // Enhanced setLanguage function that updates all storage locations and invalidates caches
  const handleSetLanguage = useCallback((newLang: Language) => {
    // Update React state
    setLanguage(newLang);

    // Force translation refresh by updating timestamp
    setChangeTimestamp(Date.now());

    // Clear any potential cached responses in the fetch API
    if (typeof caches !== 'undefined') {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      });
    }

    // Notify service worker about language change to clear translation caches
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      // Wait for service worker to be ready before sending message
      navigator.serviceWorker.ready.then(registration => {
        console.log('Notifying service worker about language change:', newLang);
        registration.active?.postMessage({
          type: 'LANGUAGE_CHANGED',
          language: newLang,
          timestamp: Date.now()
        });
      }).catch(err => console.warn('Failed to notify service worker:', err));
    }

    // Consistently update all storage mechanisms
    if (typeof window !== 'undefined') {
      // Update localStorage
      localStorage.setItem('preferred_language', newLang);

      // Update sessionStorage
      sessionStorage.setItem('preferred_language', newLang);

      // Update cookies - set with path=/ to ensure it's available across the site
      document.cookie = `preferred_language=${newLang}; path=/; max-age=31536000`; // 1 year
      document.cookie = `NEXT_LOCALE=${newLang}; path=/; max-age=31536000`;

      // Update URL by changing the language path segment instead of query parameter
      const url = new URL(window.location.href);
      const pathParts = url.pathname.split('/');

      // If the pathname starts with a language code, replace it
      if (pathParts.length > 1 && ['en', 'de', 'ro', 'fr', 'es'].includes(pathParts[1])) {
        pathParts[1] = newLang;
        url.pathname = pathParts.join('/');
        window.location.href = url.toString(); // Use full page navigation to ensure middleware processes it
      } else {
        // If no language code in the path, redirect to the homepage with the new language
        url.pathname = `/${newLang}${url.pathname}`;
        window.location.href = url.toString();
      }

      // Update HTML lang attribute
      document.documentElement.lang = newLang;
    }
  }, []);

  // Initialize language from browser storage
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set detected language
    const detectedLang = detectLanguage();
    if (detectedLang !== language) {
      setLanguage(detectedLang);
    }
  }, [detectLanguage, language]);

  // Optimized translation loading with aggressive cache busting
  useEffect(() => {
    const loadTranslations = async () => {
      setIsLoading(true);

      // If we already have cached translations, use them first while loading
      if (translationCache[language] && Object.keys(translationCache[language]).length > 0) {
        setTranslations(translationCache[language]);
      } else {
        // If no cache, set initial translations immediately for basic UI rendering
        setTranslations(initialTranslations[language]);
      }

      try {
        // Optimized loading sequence with caching

        // Try to load JSON file from public/locales first (fastest and works offline)
        if (typeof window !== 'undefined') {
          try {
            // Use AbortController to set a timeout for fetch
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 1000); // 1 second timeout

            // Create a unique cache-busting parameter
            const cacheBuster = `_=${changeTimestamp}-${Math.random().toString(36).substring(2, 15)}`;

            // Force no-cache headers
            const fetchOptions = {
              signal: controller.signal,
              cache: 'no-store' as RequestCache,
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
              }
            };

            const response = await fetch(`/locales/${language}.json?${cacheBuster}`, fetchOptions);

            clearTimeout(timeoutId);

            if (response.ok) {
              const data = await response.json();
              // Validate that this is actually a translation file
              if (typeof data === 'object' && data !== null) {
                translationCache[language] = data; // Update cache
                setTranslations(data);
                setIsLoading(false);
                return;
              }
            }
          } catch (error) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
              console.warn('Failed to load locale file from public directory:', error);
            }
          }
        }

        // If no cache hit and local file failed, try database sources

        // Try to get translations from Realtime Database (faster than Firestore)
        try {
          const rtdbRef = ref(realtimeDb, `translations/${language}`);
          const rtdbSnapshot = await get(rtdbRef);

          if (rtdbSnapshot.exists()) {
            const rtdbData = rtdbSnapshot.val();
            translationCache[language] = rtdbData; // Update cache
            setTranslations(rtdbData);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.warn('Failed to load translations from Realtime Database:', error);
        }

        // Last resort: try Firestore
        try {
          const docRef = doc(db, 'translations', language);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const firestoreData = docSnap.data() as Record<string, string>;
            translationCache[language] = firestoreData; // Update cache
            setTranslations(firestoreData);
          }
        } catch (error) {
          console.warn('Failed to load translations from Firestore:', error);
        }
      } catch (error) {
        console.error(`Error in translation loading chain for ${language}:`, error);
      } finally {
        // Ensure loading state is turned off
        setIsLoading(false);
      }
    };

    loadTranslations();

    // Update HTML lang attribute when language changes
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language, changeTimestamp]);

  // Memoize translate function to prevent unnecessary re-renders
  const translate = useCallback((key: string, defaultValue?: string): string => {
    // Fast lookup using optional chaining and nullish coalescing
    return translations?.[key] ?? defaultValue ?? key;
  }, [translations]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<LanguageContextType>(() => ({
    language,
    setLanguage: handleSetLanguage, // Use the enhanced function
    translations,
    translate,
    isLoading,
  }), [language, handleSetLanguage, translate, translations, isLoading]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
